import { CaretDownIcon, CloseIcon } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import { useField } from 'formik'
import { ChangeEvent, useState } from 'react'
import Select, { ClearIndicatorProps, components, DropdownIndicatorProps } from 'react-select'
import AsyncSelect from 'react-select/async'

import { useMobile } from '@/hooks'

import { InputContainer } from '../Input/Input.styles'
import { getDropdownStyles, getOverviewDropdownStyles } from './InputSelect.util'

export type Options = {
  label?: string
  value?: string | number
}

export interface InputSelectProps {
  async?: boolean
  className?: string
  defaultOptions?: boolean | Options[]
  disabled?: boolean
  id: string
  isLoading?: boolean
  label?: string
  loadOptions?: (val: any) => void
  name: string
  nullLabel?: string
  onChange?: (e: ChangeEvent<any>) => void
  onClear?: () => void
  optionValue?: Options
  options?: Options[]
  overviewDropdown?: boolean
  showValueAsLabel?: boolean
}

export function InputSelect({
  async = false,
  name,
  label,
  id,
  options,
  disabled,
  isLoading,
  loadOptions,
  className = '',
  onChange: onParentChange,
  onClear,
  defaultOptions = true,
  optionValue,
  overviewDropdown = false,
  showValueAsLabel = false,
  nullLabel = '',
}: InputSelectProps) {
  const [field, { touched, error }, { setValue }] = useField({ name, type: 'text', id })
  const isError = touched && !!error
  const { isMobile } = useMobile()
  const hint = isError && error
  const { value } = field
  const isClearable = !!value
  const [asyncValue, setAsyncValue] = useState<Options | undefined>(optionValue)

  const resetValues = () => {
    setValue(undefined)
    setAsyncValue(undefined)
    onClear && onClear()
  }

  const handleChange = (option) => {
    if (!option) {
      resetValues()
      return
    }

    setValue(option?.value)
    onParentChange && onParentChange(option?.value)
    if (async) {
      if (showValueAsLabel) {
        setAsyncValue({ label: option?.value + '', value: option.value })
      } else {
        setAsyncValue(option)
      }
    }
  }

  const dropdownStyles = getDropdownStyles(isError, isClearable, isMobile)
  const overviewDropdownStyles = getOverviewDropdownStyles(isError, isClearable, isMobile)

  const DropdownIndicator = (props: DropdownIndicatorProps<any, true>) => {
    return (
      <components.DropdownIndicator {...props}>
        <CaretDownIcon style={{ right: 0, marginRight: 8, top: `${!overviewDropdown ? 20 : 12}` }} />
      </components.DropdownIndicator>
    )
  }

  const ClearIndicator = (props: ClearIndicatorProps<any, true>) => {
    return (
      <components.ClearIndicator {...props}>
        <CloseIcon
          style={{ right: 0, marginRight: `${!overviewDropdown ? 28 : 4}`, top: `${!overviewDropdown ? 20 : 12}` }}
        />
      </components.ClearIndicator>
    )
  }

  return (
    <SelectContainer className={`flex flex-col ${className}`} isDisabled={disabled} isError={isError}>
      {label && (
        <label className="mb-2 font-medium leading-120 text-primary" htmlFor={id}>
          {label}
        </label>
      )}

      {async ? (
        <AsyncSelect
          components={{ DropdownIndicator: DropdownIndicator, ClearIndicator: ClearIndicator }}
          defaultInputValue={defaultOptions !== false ? field.value : ''}
          defaultOptions={defaultOptions}
          isClearable={true}
          isDisabled={disabled}
          isMulti={false}
          loadOptions={loadOptions}
          name={field.name}
          onChange={handleChange}
          styles={!overviewDropdown ? dropdownStyles : overviewDropdownStyles}
          value={
            !value || value === ''
              ? {
                  label: nullLabel + '',
                  value: '',
                }
              : !!asyncValue
                ? asyncValue
                : {
                    label: value,
                    value,
                  }
          }
        />
      ) : (
        <Select
          components={{ DropdownIndicator: DropdownIndicator }}
          isDisabled={disabled}
          isLoading={isLoading || false}
          isMulti={false}
          name={field.name}
          onChange={handleChange}
          options={options}
          styles={!overviewDropdown ? dropdownStyles : overviewDropdownStyles}
          value={options ? options.find((option) => option.value === field.value) : ''}
        />
      )}

      {hint && <p className="mt-1 font-medium text-label-xs text-tertiary">{hint}</p>}
    </SelectContainer>
  )
}

export const SelectContainer = styled(InputContainer)`
  svg:first-of-type {
    left: unset;
  }
`
